import React from 'react';
import { Lock, Users } from 'lucide-react';
import { GetProfilsResponse } from 'api/atelier-roles';
import { Chip } from 'back-office/shared/components/rolesUi';
import { SectionHeader } from './SectionHeader';

interface ProfilsTabProps {
  profils: GetProfilsResponse;
}

/**
 * Profils = modèles de rôles appliqués à la création d'une personne morale
 * (copie, pas de lien vivant). FIGÉS : profils et mapping nature → profil sont
 * pilotés par le code et resynchronisés au boot (exact-sync) — un profil
 * s'applique à chaque nouvel acteur, le rendre éditable contaminerait
 * silencieusement tous les comptes créés ensuite. Ici, consultation seule.
 */
export const ProfilsTab: React.FC<ProfilsTabProps> = ({ profils }) => (
  <div className="space-y-6">
    <SectionHeader
      icon={Users}
      title="Profils"
      subtitle="Un profil est l'ensemble de rôles appliqué à la création d'un acteur selon sa nature — il ne modifie jamais les comptes existants. Les profils sont définis par le code et resynchronisés à chaque déploiement : ils ne s'administrent pas ici."
    />

    {/* Rappel indicatif, volontairement discret : toutes les natures possibles
        et le profil appliqué à la création de chacune. */}
    <div className="rounded-xl border border-gray-200 bg-gray-50/60 px-6 py-4">
      <span className="mb-3 block text-xs font-medium uppercase tracking-wide text-gray-400">
        Natures de personne morale
      </span>
      <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2 xl:grid-cols-5">
        {profils.natures.map((mapping) => {
          const code = mapping.sousNature
            ? `${mapping.nature}/${mapping.sousNature}`
            : mapping.nature;
          // Libellé serveur (source de vérité) — composé pour les sous-natures.
          const label = mapping.libelle;
          return (
            <div key={code}>
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-semibold text-gray-900">{code}</span>
                <span className="text-xs text-gray-500">
                  {mapping.profilNom ? `→ profil ${mapping.profilNom}` : '→ défauts du code'}
                </span>
              </div>
              {label && <p className="text-xs text-gray-500">{label}</p>}
            </div>
          );
        })}
      </div>
    </div>

    <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
      {profils.profils.map((profil) => (
        <div key={profil.id} className="border rounded-xl bg-white shadow-sm overflow-hidden">
          <header className="flex items-center justify-between gap-3 bg-primary px-6 py-4">
            <h3 className="truncate text-base font-semibold text-white">{profil.nom}</h3>
            <span
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600"
              title="Défini par le code — resynchronisé à chaque déploiement"
            >
              <Lock className="h-3 w-3" /> Figé
            </span>
          </header>
          <div className="space-y-3 px-6 py-4">
            {profil.description && <p className="text-xs text-gray-500">{profil.description}</p>}
            <div>
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-400">
                Rôles portés
              </span>
              <div className="flex flex-wrap gap-1">
                {profil.roles.map((role) => (
                  <Chip key={role} label={role} />
                ))}
              </div>
            </div>
            {profil.natures.length > 0 && (
              <p className="text-xs text-gray-500">
                Appliqué à la création des natures :{' '}
                <span className="font-medium text-gray-700">{profil.natures.join(', ')}</span>
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);
